import React, { useState, useEffect, useRef } from 'react';
import { sendMessage, getChatHistory, MessageHistoryItem, clearChatHistory } from '../services/api'; // Import getChatHistory, MessageHistoryItem and clearChatHistory

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface ChatProps {
  assistantName?: string;
  onMessageSent?: () => void;
}

const Chat = ({ assistantName = 'Asistente', onMessageSent }: ChatProps) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true); // New state for history loading
  const messagesEndRef = useRef<null | HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(scrollToBottom, [messages]);

  // New useEffect to load chat history
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setHistoryLoading(true);
        const history = await getChatHistory();
        if (history) {
          // Map MessageHistoryItem to Message interface
          setMessages(history.map(item => ({ role: item.role as 'user' | 'assistant', content: item.content })));
        }
      } catch (error) {
        console.error("Error loading chat history:", error);
        // Optionally display an error message to the user
      } finally {
        setHistoryLoading(false);
      }
    };
    fetchHistory();
  }, []); // Empty dependency array means this runs once on mount

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMessage: Message = { role: 'user', content: input };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await sendMessage(userMessage.content);
      if (response) {
        const assistantMessage: Message = {
          role: 'assistant',
          content: response.response,
        };
        setMessages((prev) => [...prev, assistantMessage]);
        if (onMessageSent) onMessageSent();
      }
    } catch (error: any) {
      const errorMessage: Message = {
        role: 'assistant',
        content: `Error: ${error.message}`,
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleClearChat = async () => {
    if (window.confirm('¿Estás seguro de que deseas limpiar el historial del chat?')) {
      const success = await clearChatHistory();
      if (success) {
        setMessages([]);
      }
    }
  };

  return (
    <div className="card mt-4">
      <div className="card-header d-flex justify-content-between align-items-center">
        <span>Chatea con {assistantName}</span>
        <button
          className="btn btn-sm btn-outline-danger"
          onClick={handleClearChat}
          disabled={loading || historyLoading || messages.length === 0}
        >
          Limpiar Chat
        </button>
      </div>
      <div className="card-body" style={{ height: '400px', overflowY: 'auto' }}>
        {historyLoading ? (
          <p className="text-center">Cargando historial de chat...</p>
        ) : messages.length === 0 ? (
          <p className="text-center text-muted">¡Comienza una conversación con {assistantName}!</p>
        ) : (
          messages.map((msg, index) => (
            <div key={index} className={`mb-3 ${msg.role === 'user' ? 'text-end' : 'text-start'}`}>
              <span className={`badge ${msg.role === 'user' ? 'bg-primary' : 'bg-secondary'}`}>
                {msg.role === 'user' ? 'Tú' : assistantName}
              </span>
              <div className="p-2 rounded" style={{ display: 'inline-block', backgroundColor: '#f1f1f1' }}>
                {msg.content}
              </div>
            </div>
          ))
        )}
        {loading && <div className="text-center">{assistantName} está pensando...</div>}
        <div ref={messagesEndRef} />
      </div>
      <div className="card-footer">
        <form onSubmit={handleSendMessage}>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Escribe tu mensaje..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={loading || historyLoading}
            />
            <button className="btn btn-primary" type="submit" disabled={loading || historyLoading}>
              Enviar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Chat;
